import { useState } from 'react';
import axios from 'axios';
import {
  Button, Typography, Space, App as AntApp, Flex,
} from 'antd';
import {
  DeleteOutlined, UploadOutlined,
} from '@ant-design/icons';
import { API_URL } from '../../config';
import type { EmpresaDados } from './types';
import { getToken } from './utils';

const { Text } = Typography;

// Uploader do logo da empresa (S3 via presigned URL).
export default function LogoUploader({ dados, setDados }: {
  dados: EmpresaDados;
  setDados: (d: EmpresaDados | ((prev: EmpresaDados) => EmpresaDados)) => void;
}) {
  const { message, modal } = AntApp.useApp();
  const [enviando, setEnviando] = useState(false);

  async function uploadFile(file: File) {
    if (!file.type.startsWith('image/')) { message.error('Arquivo não é uma imagem.'); return; }
    if (file.size > 5 * 1024 * 1024) { message.error('Imagem maior que 5MB.'); return; }
    setEnviando(true);
    try {
      const token = await getToken();
      const r1 = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'adm_upload_url_logo', filename: file.name, content_type: file.type },
        { headers: { Authorization: token } });
      const d1 = r1.data.body ? JSON.parse(r1.data.body) : r1.data;
      await axios.put(d1.upload_url, file, { headers: { 'Content-Type': file.type } });
      const r2 = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'adm_salvar_empresa', logo_s3_key: d1.s3_key },
        { headers: { Authorization: token } });
      const d2 = r2.data.body ? JSON.parse(r2.data.body) : r2.data;
      setDados(prev => ({ ...prev, ...d2 }));
      message.success('Logo enviado.');
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setEnviando(false); }
  }

  function remover() {
    modal.confirm({
      title: 'Remover o logo?', content: 'O documento de venda volta a usar o logo padrão.',
      okText: 'Remover', cancelText: 'Cancelar', okButtonProps: { danger: true },
      onOk: async () => {
        setEnviando(true);
        try {
          const token = await getToken();
          const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
            { action: 'adm_salvar_empresa', logo_s3_key: null },
            { headers: { Authorization: token } });
          const d = r.data.body ? JSON.parse(r.data.body) : r.data;
          setDados(prev => ({ ...prev, ...d, logo_url: null }));
        } catch (e: any) {
          message.error('Erro: ' + (e.response?.data?.error || e.message));
        } finally { setEnviando(false); }
      },
    });
  }

  return (
    <Flex align="center" gap={18}>
      <div style={{
        width: 120, height: 120, border: '0.5px dashed #aaa', borderRadius: 8,
        background: '#FAFBFF', display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden',
      }}>
        {dados.logo_url
          ? <img src={dados.logo_url} alt="logo" style={{ maxWidth: '100%', maxHeight: '100%' }} />
          : <Text type="secondary" style={{ fontSize: 11 }}>sem logo</Text>}
      </div>
      <div style={{ flex: 1 }}>
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
          Logo aparece no cabeçalho de propostas, vendas e contratos. PNG/JPG/WebP, máximo 5MB. ~300×120px.
        </Text>
        <Space>
          <Button type="primary" icon={<UploadOutlined />} loading={enviando}>
            <label style={{ cursor: 'pointer' }}>
              {dados.logo_url ? 'Trocar logo' : 'Enviar logo'}
              <input type="file" accept="image/*" disabled={enviando} style={{ display: 'none' }}
                onChange={e => { const f = e.target.files?.[0]; if (f) uploadFile(f); }} />
            </label>
          </Button>
          {dados.logo_url && !enviando && (
            <Button danger icon={<DeleteOutlined />} onClick={remover}>Remover</Button>
          )}
        </Space>
      </div>
    </Flex>
  );
}
